import type { ReactNode } from 'react';
import { useState, useCallback, useEffect, useContext } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiUser } from '@wix/da-papi-types';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import IconTrashcan from '@wix/da-ds/pkg/Icons/24x24/Trashcan';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import {
  BiData,
  BiEvent,
  SeeAllClickBiEvent,
  WidgetRemoveConfirmBiEvent,
} from '@wix/da-bi/pkg/events';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';

import WidgetHeader from '../WidgetHeader/WidgetHeader';
import WidgetEditControls from '../WidgetEditControls';
import WidgetHeaderMobile from '../WidgetHeaderMobile';
import { useConfirmationModal } from '../../_hoc/moduleHooks';
import { GallectionType } from '../../../../../types/gallection';
import { ModuleType } from '../../../../../types/modules';
import { ZoneType } from '../../../../../types/layout';
import { UpdateModulePayload } from '../../../../actions/modules';
import WidgetBoxWrapper from '../WidgetBoxWrapper';

import s from './WidgetBox.scss';

export interface Props {
  moduleId: number;
  moduleType: ModuleType;
  moduleVersion: number;
  position?: number;
  zone: ZoneType;
  section: string;
  children: ReactNode;
  className?: string;
  direction?: 'row' | 'column';
  widgetBoxClassName?: string;
  header: string | ReactNode;
  headerClassName?: string;
  headerExtras?: ReactNode | ((props: any) => ReactNode);
  count?: number | string;
  allowEditOnMobile?: boolean;
  autoShowWidgetConfig?: boolean;
  isAuthorizedToEditWidget?: boolean;
  isAuthorizedToRemoveWidget?: boolean;
  isNewlyAddedModule?: boolean;
  isOwner: boolean;
  showCoreUpsell?: boolean;
  isAuthorizedToMoveModule: boolean;
  isEmpty?: boolean;
  emptyView?: ReactNode;
  hasMore?: boolean;
  profileOwnerUser: PapiUser;
  seeAllUrl?: string;
  seeAllText?: string;
  widgetMenuRenderer?: (closeMenu: () => void) => ReactNode;
  widgetMoveUp?: () => void;
  widgetMoveDown?: () => void;
  widgetRemove?: () => void;
  widgetAddColumn?: () => void;
  editControlsTooltipTitle?: string;
  editControlsTooltipContent?: ReactNode;
  updateModule?: (payload: UpdateModulePayload) => void;
  sourceType?: GallectionType;
  dataHook?: string;
  biData?: Partial<BiEvent>;
}

export function WidgetBox(props: Props) {
  const { t } = useTranslation();
  const [isWidgetConfigShown, setIsWidgetConfigShown] = useState(false);
  const [disableAutoShowWidgetConfigFor, setDisableAutoShowWidgetConfigFor] =
    useState<number[]>([]);
  const { pushConfirmationModal } = useConfirmationModal();
  const isMobile = useContext(MobileContext);

  const {
    moduleId,
    className,
    count,
    children,
    isEmpty,
    emptyView,
    moduleType,
    zone,
    dataHook,
    biData,
    profileOwnerUser,
    headerClassName,
    headerExtras,
    widgetMenuRenderer,
    autoShowWidgetConfig,
    isNewlyAddedModule,
    updateModule,
    isOwner,
    isAuthorizedToEditWidget,
    isAuthorizedToRemoveWidget,
    allowEditOnMobile,
    widgetMoveUp,
    widgetMoveDown,
    widgetRemove,
    widgetAddColumn,
    editControlsTooltipTitle,
    editControlsTooltipContent,
    isAuthorizedToMoveModule,
    direction,
    seeAllUrl,
    hasMore,
    seeAllText,
  } = props;

  // This is why mixing redux and local state is an anti-pattern.
  // Due to race condition between updating isNewlyAddedModule in the redux
  // store and the isWidgetConfigShown in the local state, this was
  // auto-showing the config even after you close it.
  // To fix it, we introduce another layer of complexity: disableAutoShowWidgetConfigFor
  // Which we use to disable the autoShowWidgetConfig for certain moduleIds,
  // after you close the config for the first time, so that it does not trigger again
  useEffect(() => {
    if (
      !isWidgetConfigShown &&
      isNewlyAddedModule &&
      !disableAutoShowWidgetConfigFor.includes(moduleId) &&
      autoShowWidgetConfig &&
      widgetMenuRenderer
    ) {
      setIsWidgetConfigShown(true);
    }
  }, [
    isWidgetConfigShown,
    isNewlyAddedModule,
    disableAutoShowWidgetConfigFor,
    moduleId,
    autoShowWidgetConfig,
    widgetMenuRenderer,
  ]);

  const showWidgetConfig = useCallback(() => {
    setIsWidgetConfigShown(true);
  }, []);

  const hideWidgetConfig = useCallback(() => {
    if (isNewlyAddedModule && updateModule) {
      updateModule({
        id: moduleId,
        moduleMeta: { isNewlyAdded: false },
      });
    }
    setIsWidgetConfigShown(false);
    setDisableAutoShowWidgetConfigFor(prev => [...prev, moduleId]);
  }, [isNewlyAddedModule, updateModule, moduleId]);

  const doWidgetRemove = useCallback(() => {
    setDisableAutoShowWidgetConfigFor(prev =>
      prev.filter(id => id !== moduleId)
    );
    widgetRemove?.();
  }, [moduleId, widgetRemove]);

  const handleWidgetRemove = useCallback(() => {
    switch (moduleType) {
      case ModuleType.CUSTOM:
      case ModuleType.MEGABOX:
      case ModuleType.DONATIONS:
        // These will destroy content if removed, show warning
        pushConfirmationModal({
          title: t('widgets.common.removeWidget.header'),
          text: t('widgets.common.removeWidget.body.danger'),
          confirmBtnLabel: t('widgets.common.removeWidget.confirmBtnLabel'),
          confirmBtnBiData: BiData<WidgetRemoveConfirmBiEvent>({
            evid: 270,
            widgetname: moduleType,
            widgetzone: zone,
          }),
          cancelBtnLabel: t('widgets.common.removeWidget.cancelBtnLabel'),
          icon: <IconTrashcan size={IconSize.MEGA} />,
          onConfirm: doWidgetRemove,
        });
        break;
      case ModuleType.ABOUT:
      default:
        pushConfirmationModal({
          title: t('widgets.common.removeWidget.header'),
          text: t('widgets.common.removeWidget.body'),
          confirmBtnLabel: t('widgets.common.removeWidget.confirmBtnLabel'),
          confirmBtnBiData: BiData<WidgetRemoveConfirmBiEvent>({
            evid: 270,
            widgetname: moduleType,
            widgetzone: zone,
          }),
          cancelBtnLabel: t('widgets.common.removeWidget.cancelBtnLabel'),
          icon: <IconTrashcan size={IconSize.MEGA} />,
          onConfirm: doWidgetRemove,
        });
        break;
    }
  }, [moduleType, zone, t, pushConfirmationModal, doWidgetRemove]);

  const renderHeaderContents = () => {
    const { header } = props;
    if (!seeAllUrl) {
      return header;
    }
    return (
      <BiLink
        biData={BiData<SeeAllClickBiEvent>({
          evid: 106,
          creatorid: profileOwnerUser.useridUuid,
          typeid: undefined,
          itemid: undefined,
        })}
        href={seeAllUrl}
      >
        {header}
      </BiLink>
    );
  };

  const renderSeeAllLink = (
    contents: ReactNode,
    role?: string,
    linkClassName?: string
  ) => {
    if (seeAllUrl && contents) {
      return (
        <TextButton
          variant="main"
          size="medium"
          biData={BiData<SeeAllClickBiEvent>({
            evid: 106,
            typeid: undefined,
            itemid: undefined,
            creatorid: profileOwnerUser?.useridUuid,
          })}
          href={seeAllUrl}
          {...(linkClassName && { className: linkClassName })}
          {...(role && { 'data-role': role })}
        >
          {contents}
        </TextButton>
      );
    }
  };

  const renderRightSideSeeAllLink = () => {
    const shouldRenderSeeAllLink = hasMore === undefined || hasMore;
    const contents = seeAllText || t('common.see_all');
    const role = 'see-all-link';
    const linkClass = s['see-all'];
    return shouldRenderSeeAllLink
      ? renderSeeAllLink(contents, role, linkClass)
      : null;
  };

  const renderEditControls = () => {
    const canUserMoveWidget = isOwner || isAuthorizedToMoveModule;
    const isAuthorized =
      canUserMoveWidget ||
      isAuthorizedToRemoveWidget ||
      isAuthorizedToEditWidget;
    const isDisabledOnMobile = isMobile && !allowEditOnMobile;

    if (!isAuthorized || isDisabledOnMobile) {
      return null;
    }

    return (
      <WidgetEditControls
        className={s['edit-widget-controls']}
        onMoveUp={widgetMoveUp}
        onMoveDown={widgetMoveDown}
        onRemove={
          isAuthorizedToRemoveWidget && widgetRemove
            ? handleWidgetRemove
            : undefined
        }
        onEdit={
          isAuthorizedToEditWidget && widgetMenuRenderer
            ? showWidgetConfig
            : undefined
        }
        onAddColumn={widgetAddColumn}
        tooltipTitle={editControlsTooltipTitle}
        tooltipContent={editControlsTooltipContent}
        direction={direction}
      />
    );
  };

  const renderWidgetConfig = () => {
    if (!widgetMenuRenderer) {
      return null;
    }
    if (isWidgetConfigShown) {
      return widgetMenuRenderer(hideWidgetConfig);
    }
    return null;
  };

  const renderHeaderExtraElements = () => (
    <div className={s['header-extras']}>
      {typeof headerExtras === 'function' ? headerExtras(props) : headerExtras}
      {renderEditControls()}
      {renderWidgetConfig()}
      {renderRightSideSeeAllLink()}
    </div>
  );

  if (isEmpty && !emptyView) {
    return null;
  }

  const headerContents = renderHeaderContents();
  return (
    <BiLoggerContextProvider
      value={{
        widgetname: moduleType,
        sectionname: moduleType,
        widgetzone: zone,
        ...biData,
      }}
    >
      <WidgetBoxWrapper
        data-role="widget"
        className={classnames(s['root'], className)}
        data-hook={dataHook}
        data-moduleid={moduleId}
        data-userid={profileOwnerUser.userId}
        id={`module-${moduleId}`}
      >
        <ErrorBoundary debugComponent="WidgetBox">
          {isMobile ? (
            <WidgetHeaderMobile
              rootClassName={headerClassName}
              header={headerContents}
              count={count}
              isSticky={false}
              extras={renderHeaderExtraElements()}
            />
          ) : (
            <WidgetHeader
              className={headerClassName}
              header={headerContents}
              count={count}
              extras={renderHeaderExtraElements()}
            />
          )}
          <div className={s['widget-content']}>
            {isEmpty ? emptyView : children}
          </div>
        </ErrorBoundary>
      </WidgetBoxWrapper>
    </BiLoggerContextProvider>
  );
}

WidgetBox.displayName = 'WidgetBox';

export default WidgetBox;
